// The path builder every package shares (web, mobile, server, the CUBE Config app). No imports, so any of them can
// load it under its own compiler settings.

/** The `:name` segments of a path pattern: `"/invoices/:invoiceId/pay"` → `"invoiceId"`. A name runs to the next `/`. */
export type PathParamName<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | PathParamName<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

/**
 * `pattern` with each `:name` segment replaced by its value, URL-encoded: `fillPath("/invoices/:invoiceId/pay",
 * { invoiceId })`. For a path that neither the typed api client (`api.url`, `routeUrl`) nor react-router's
 * `generatePath` builds: server-side links, external APIs, the CUBE Config app. `pattern` is a path; an absolute URL's
 * port would read as a segment. Throws when a segment has no value, as `generatePath` does.
 */
export function fillPath<P extends string>(
  pattern: P,
  params: Readonly<Record<PathParamName<P>, string | number>>,
): string {
  const values = new Map<string, string | number>(Object.entries(params));
  return pattern.replace(/:([^/]+)/g, (segment, name: string) => {
    const value = values.get(name);
    if (value === undefined) throw new Error(`${pattern}: no value for ${segment}`);
    return encodeURIComponent(value);
  });
}
